import { motion } from 'framer-motion';
import {
  FaEye,
  FaEnvelope,
  FaWhatsapp,
  FaEdit,
  FaImage,
  FaPhone,
} from 'react-icons/fa';
import { useAuth } from '@/contexts/AuthContext';

interface StatItem {
  name: string;
  value: string;
  change: string;
  changeType: 'increase' | 'decrease';
  icon: React.ComponentType<{ className?: string }>;
}

const stats: StatItem[] = [
  {
    name: 'Visualizações',
    value: '2,345',
    change: '+12%',
    changeType: 'increase',
    icon: FaEye,
  },
  {
    name: 'Mensagens',
    value: '48',
    change: '+8%',
    changeType: 'increase',
    icon: FaEnvelope,
  },
  {
    name: 'WhatsApp',
    value: '156',
    change: '+24%',
    changeType: 'increase',
    icon: FaWhatsapp,
  },
];

interface QuickAction {
  name: string;
  description: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const quickActions: QuickAction[] = [
  {
    name: 'Editar Conteúdo',
    description: 'Atualizar textos e informações do site',
    href: '/admin/content',
    icon: FaEdit,
  },
  {
    name: 'Gerenciar Imagens',
    description: 'Atualizar imagens e galeria',
    href: '/admin/content/images',
    icon: FaImage,
  },
  {
    name: 'Atualizar Contatos',
    description: 'Modificar informações de contato',
    href: '/admin/contacts',
    icon: FaPhone,
  },
];

export default function AdminDashboard() {
  const { logout } = useAuth();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <button
          onClick={logout}
          className="rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          Sair
        </button>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg bg-white p-6 shadow-md">
          <h2 className="text-lg font-semibold text-gray-900">Total de Projetos</h2>
          <p className="mt-2 text-3xl font-bold text-primary-600">12</p>
        </div>
        <div className="rounded-lg bg-white p-6 shadow-md">
          <h2 className="text-lg font-semibold text-gray-900">Contatos Recebidos</h2>
          <p className="mt-2 text-3xl font-bold text-primary-600">48</p>
        </div>
        <div className="rounded-lg bg-white p-6 shadow-md">
          <h2 className="text-lg font-semibold text-gray-900">Clientes Ativos</h2>
          <p className="mt-2 text-3xl font-bold text-primary-600">32</p>
        </div>
        <div className="rounded-lg bg-white p-6 shadow-md">
          <h2 className="text-lg font-semibold text-gray-900">Orçamentos Pendentes</h2>
          <p className="mt-2 text-3xl font-bold text-primary-600">7</p>
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <stat.icon className="h-6 w-6 text-gray-400" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="truncate text-sm font-medium text-gray-500">
                    {stat.name}
                  </dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">
                      {stat.value}
                    </div>
                  </dd>
                </dl>
              </div>
              <div className="ml-5">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    stat.changeType === 'increase'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {stat.change}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="rounded-lg bg-white p-6 shadow-md">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Atividade Recente</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b pb-4">
            <div>
              <p className="font-medium text-gray-900">Novo contato recebido</p>
              <p className="text-sm text-gray-500">João Silva solicitou orçamento</p>
            </div>
            <span className="text-sm text-gray-500">Há 2 horas</span>
          </div>
          <div className="flex items-center justify-between border-b pb-4">
            <div>
              <p className="font-medium text-gray-900">Projeto atualizado</p>
              <p className="text-sm text-gray-500">Estrutura metálica finalizada</p>
            </div>
            <span className="text-sm text-gray-500">Há 5 horas</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Novo projeto iniciado</p>
              <p className="text-sm text-gray-500">Serralheria industrial</p>
            </div>
            <span className="text-sm text-gray-500">Há 1 dia</span>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <div className="mb-6 border-b border-gray-200">
          <div className="flex items-center py-5">
            <div className="min-w-0 flex-1 px-4 sm:px-6">
              <h2 className="text-lg font-medium leading-6 text-gray-900">
                Ações Rápidas
              </h2>
            </div>
          </div>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {quickActions.map((action, index) => (
            <motion.div
              key={action.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <a
                href={action.href}
                className="block overflow-hidden rounded-lg bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <action.icon
                      className="h-8 w-8 text-primary-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-4">
                    <p className="text-base font-medium text-gray-900">
                      {action.name}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {action.description}
                    </p>
                  </div>
                </div>
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}